import { useEffect, useMemo, useState } from "react";

export const GITHUB_URL = "https://github.com/sukritgolinks";
const ENDPOINT = "https://github-contributions-api.jogruber.de/v4/sukritgolinks?y=last";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["", "Mon", "", "Wed", "", "Fri", ""];

const toWeeks = (days) => {
  if (!days.length) return [];
  const start = new Date(`${days[0].date}T12:00:00`);
  const pad = start.getDay();
  const cells = [...Array(pad).fill(null), ...days];
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  while (weeks[weeks.length - 1]?.length < 7) weeks[weeks.length - 1].push(null);
  return weeks;
};

const formatDay = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export const GithubChart = () => {
  const [days, setDays] = useState([]);
  const [total, setTotal] = useState(null);
  const [status, setStatus] = useState("loading");
  const [tip, setTip] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    fetch(ENDPOINT, { signal: ctrl.signal })
      .then((res) => {
        if (!res.ok) throw new Error("bad status");
        return res.json();
      })
      .then((data) => {
        const list = Array.isArray(data.contributions) ? data.contributions : [];
        setDays(list);
        setTotal(data.total?.lastYear ?? list.reduce((sum, day) => sum + (day.count || 0), 0));
        setStatus("ready");
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setStatus("error");
      });
    return () => ctrl.abort();
  }, []);

  const weeks = useMemo(() => toWeeks(days), [days]);
  const monthMarks = useMemo(() => {
    const marks = [];
    weeks.forEach((week, i) => {
      const first = week.find(Boolean);
      if (!first) return;
      const month = Number(first.date.slice(5, 7)) - 1;
      if (!marks.length || marks[marks.length - 1].month !== month) {
        marks.push({ i, month, label: MONTHS[month] });
      }
    });
    return marks;
  }, [weeks]);

  return (
    <aside className={`gh-wrap is-${status}`}>
      <div className="gh-copy">
        <p className="gh-kicker">GitHub</p>
        <h3 className="gh-title">PM who still ships.</h3>
        <p className="gh-lede">
          {status === "ready" && total != null ? (
            <>
              <strong>{total.toLocaleString()}</strong> contributions last year. I own the
              roadmap, and I open the editor when that’s the fastest way to land it.
            </>
          ) : status === "error" ? (
            <>The graph didn’t load. The commits are still on GitHub.</>
          ) : (
            <>Pulling the last year of commits…</>
          )}
        </p>
        <a className="gh-link" href={GITHUB_URL} target="_blank" rel="noopener noreferrer">
          sukritgolinks
        </a>
      </div>

      {status === "error" ? (
        <a className="gh-fallback" href={GITHUB_URL} target="_blank" rel="noopener noreferrer">
          <img
            src="https://ghchart.rshah.org/111c36/sukritgolinks"
            alt="GitHub contribution chart for sukritgolinks"
          />
        </a>
      ) : (
        <div className="gh-board" onMouseLeave={() => setTip(null)}>
          <div className="gh-months" aria-hidden="true">
            {monthMarks.map((mark) => (
              <span key={`${mark.label}-${mark.i}`} style={{ gridColumn: mark.i + 1 }}>
                {mark.label}
              </span>
            ))}
          </div>
          <div className="gh-weekdays" aria-hidden="true">
            {WEEKDAYS.map((label, i) => (
              <span key={`${label}-${i}`}>{label}</span>
            ))}
          </div>
          <div className="gh-grid" role="img" aria-label="GitHub contributions in the last year">
            {(weeks.length ? weeks : Array.from({ length: 53 }, () => Array(7).fill(null))).map((week, wi) =>
              week.map((day, di) =>
                day ? (
                  <button
                    key={day.date}
                    type="button"
                    className={`gh-day lv-${day.level}`}
                    aria-label={`${day.count} contribution${day.count === 1 ? "" : "s"} on ${formatDay(day.date)}`}
                    onMouseEnter={(e) => {
                      const box = e.currentTarget.getBoundingClientRect();
                      setTip({
                        count: day.count,
                        date: formatDay(day.date),
                        x: box.left + box.width / 2,
                        y: box.top,
                      });
                    }}
                  />
                ) : (
                  <span key={`empty-${wi}-${di}`} className="gh-day is-empty" />
                )
              )
            )}
          </div>
          {tip && (
            <div className="gh-tip" style={{ left: tip.x, top: tip.y }} role="status">
              <strong>
                {tip.count} contribution{tip.count === 1 ? "" : "s"}
              </strong>
              <span>{tip.date}</span>
            </div>
          )}
        </div>
      )}

      <p className="gh-legend" aria-hidden="true">
        Less
        <i className="lv-0" />
        <i className="lv-1" />
        <i className="lv-2" />
        <i className="lv-3" />
        <i className="lv-4" />
        More
      </p>
    </aside>
  );
};
